import { Archive, ArchiveRestore, Pencil, Pill, Search } from 'lucide-react'
import { useState } from 'react'

import type { CatalogProduct } from '@snootsvet/shared'

import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Input } from '@/frontend/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import { CatalogProductForm } from '@/frontend/features/inventory/catalog-product.form'
import { useListCatalogProducts } from '@/frontend/features/inventory/use-list-catalog-products'
import { useUpdateCatalogProduct } from '@/frontend/features/inventory/use-update-catalog-product'
import { useDebounce } from '@/frontend/hooks/use-debounce'

function EditCatalogProductDialog({
  product,
  open,
  onOpenChange,
}: {
  product: CatalogProduct
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Catalog Product</DialogTitle>
        </DialogHeader>
        <CatalogProductForm
          catalogProductId={product.id}
          defaultValues={{
            name: product.name,
            formulation: product.formulation,
            unit: product.unit ?? '',
            isControlledSubstance: product.isControlledSubstance,
          }}
          onSuccess={() => onOpenChange(false)}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

function CatalogProductTable({ products }: { products: CatalogProduct[] }) {
  const [editingProduct, setEditingProduct] = useState<CatalogProduct | null>(null)
  const { mutate: toggleArchive, isPending } = useUpdateCatalogProduct()

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Formulation</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead className="text-center">Controlled</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => {
            const isArchived = !!product.archivedAt
            return (
              <TableRow key={product.id} className={isArchived ? 'opacity-50' : undefined}>
                <TableCell className="font-medium">{product.name}</TableCell>
                <TableCell>{product.formulation}</TableCell>
                <TableCell>
                  <span className="text-muted-foreground">{product.unit || '—'}</span>
                </TableCell>
                <TableCell className="text-center">
                  {product.isControlledSubstance && (
                    <Badge variant="destructive" size="sm">
                      CD
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {isArchived ? (
                    <Badge variant="secondary" size="sm">
                      Archived
                    </Badge>
                  ) : (
                    <Badge variant="success" size="sm">
                      Active
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setEditingProduct(product)}>
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={isPending}
                      className={isArchived ? 'text-success-fg' : 'text-destructive-fg'}
                      onClick={() =>
                        toggleArchive({
                          id: product.id,
                          archivedAt: isArchived ? null : new Date().toISOString(),
                        })
                      }
                    >
                      {isArchived ? (
                        <ArchiveRestore className="size-3.5" />
                      ) : (
                        <Archive className="size-3.5" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {editingProduct && (
        <EditCatalogProductDialog
          product={editingProduct}
          open={true}
          onOpenChange={(open) => {
            if (!open) setEditingProduct(null)
          }}
        />
      )}
    </>
  )
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
]

export function CatalogProductsAdminPage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'active' | 'archived' | 'all'>('active')
  const debouncedSearch = useDebounce(search, 250)
  const { data, isLoading, error } = useListCatalogProducts({
    search: debouncedSearch || undefined,
    status,
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-headline-lg">Product Catalog</h1>
        <Button size="lg" onClick={() => setDialogOpen(true)}>
          <Pill />
          Add Product
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={(v) => v && setStatus(v)} items={STATUS_OPTIONS}>
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : error ? (
        <p className="text-destructive-fg">Error: {error.message}</p>
      ) : data?.length === 0 ? (
        <p className="text-muted-foreground">No catalog products yet.</p>
      ) : (
        <CatalogProductTable products={data ?? []} />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Catalog Product</DialogTitle>
          </DialogHeader>
          <CatalogProductForm
            onSuccess={() => setDialogOpen(false)}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
